import { startCase } from "lodash";
import { createSelector } from "reselect";
import { v4 as uuidv4 } from "uuid";

import type { TeamsGroup } from "rivals/components/pages/Teams/TeamsGroupsList/types";
import { MultiselectLabelType } from "rivals/components/shared/Form/MultiSelectTypeahead/types";
import { SelectOption } from "rivals/components/shared/Form/Select/types";
import { State as AppState, StateKeys } from "rivals/redux/reducer";
import { createDeepEqualSelector } from "rivals/redux/selector";
import { ConferenceGroupName } from "rivals/services/fetchConferences/types";
import { CollegeLetterGroup } from "rivals/shared/interfaces/College";
import {
  Conference,
  ConferenceGroups,
  isConference
} from "rivals/shared/interfaces/Conference";
import { MultiSelectType } from "rivals/shared/interfaces/search";
import { State } from "./reducer";

export const getConferenceState = (state: AppState): State =>
  state[StateKeys.CONFERENCE];

export const selectCollegeLetterGroups = createSelector(
  getConferenceState,
  state => state.collegeLetterGroups
);

export const selectConferences = createSelector(
  getConferenceState,
  state => state.conferences
);

export const selectConference = createSelector(
  getConferenceState,
  state => state.conference
);

export const selectConferenceGroups = createSelector(
  getConferenceState,
  state => state.conferenceGroups
);

export const selectSearching = createSelector(
  getConferenceState,
  state => state.searching
);

const conferenceOptionsUpperCase = (
  conferences?: Conference[]
): SelectOption<string>[] => {
  const confOptions: SelectOption<string>[] = [];

  if (conferences) {
    conferences?.forEach(({ name, abbreviation }) => {
      confOptions.push({
        name: name.toLocaleUpperCase(),
        value: abbreviation
      });
    });
    confOptions?.unshift({ name: "ALL TEAMS", value: "all-teams" });
  }

  return confOptions;
};

export const selectConferenceOptions = createDeepEqualSelector(
  selectConferences,
  conferenceOptionsUpperCase
);

const getConferenceOptions = (conferences: Conference[]): MultiSelectType[] =>
  conferences.map(
    (conference: Conference): MultiSelectType => {
      return {
        label: conference.name,
        value: conference.abbreviation.toLocaleUpperCase()
      };
    }
  );

const formatGroupName = (groupName: ConferenceGroupName): string => {
  return groupName !== "nonGrouped" ? startCase(groupName) : "";
};

const getConferenceGroupOptions = (
  conferenceGroups?: ConferenceGroups
): MultiselectLabelType[] => {
  const selectGroupsOptions: MultiselectLabelType[] = [];

  if (conferenceGroups) {
    for (const key in conferenceGroups) {
      const groupName = key as ConferenceGroupName;
      const label = formatGroupName(groupName);

      selectGroupsOptions.push({
        label,
        options: getConferenceOptions(conferenceGroups[groupName])
      });
    }
  }

  return selectGroupsOptions;
};

export const selectConferenceGroupOptions = createDeepEqualSelector(
  selectConferenceGroups,
  getConferenceGroupOptions
);

const formatConferencesAsTeamsGroups = (
  conferences?: Conference[] | CollegeLetterGroup[]
): TeamsGroup[] | undefined =>
  conferences?.map(conference => {
    const teams = isConference(conference)
      ? conference.colleges
      : conference.items;
    return {
      id: `group-${uuidv4()}`,
      teams: teams?.map(college => ({
        color: college.color,
        id: `team-${uuidv4()}`,
        logo: college.largeTeamLogo,
        name: college.commonName,
        seoUrls: college.seoUrls
      })),
      title: isConference(conference) ? conference.name : conference.title
    };
  });

export const selectConferencesAsTeamGroups = createSelector(
  selectConferences,
  conferences => formatConferencesAsTeamsGroups(conferences)
);

export const selectCollegeLetterGroupsAsTeamGroups = createSelector(
  selectCollegeLetterGroups,
  conferences => formatConferencesAsTeamsGroups(conferences)
);

export const selectConferenceAsTeamGroups = createSelector(
  selectConference,
  conference => (conference ? formatConferencesAsTeamsGroups([conference]) : [])
);
